import {
	Divider,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { capitalize } from 'lodash'

import { TranslationHelper } from '@/context/Locale'
import {
	ObjectTypesFieldSet,
	StyledLegend,
} from '@/pages/User/pages/Home/components/TreePanel/components/styles'

export interface DateTimeOptionsProps {
	generateFromLastTemplate: boolean
	generateFromLastVersion: boolean
	setGenerateFromLastTemplate: React.Dispatch<React.SetStateAction<boolean>>
	setGenerateFromLastVersion: React.Dispatch<React.SetStateAction<boolean>>
	setTemplateDate: React.Dispatch<React.SetStateAction<Date | null>>
	setVersionDate: React.Dispatch<React.SetStateAction<Date | null>>
	t: TranslationHelper
	templateDate: Date | null
	versionDate: Date | null
}

const DATE_PICKER_SX = {
	fontSize: '0.875rem',
	'& *': {
		fontSize: '0.875rem !important',
	},
	width: '100%',
}

const TEXT_FIELD_SX = {
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none',
	},
	'& .MuiInputBase-input': {
		fontSize: '0.875rem !important',
	},
	'& input': {
		fontSize: '0.875rem !important',
	},
}

const DATE_PICKER_SLOT_PROPS = {
	textField: {
		size: 'small' as const,
		sx: TEXT_FIELD_SX,
	},
}

export const DateTimeOptions = ({
	generateFromLastTemplate,
	generateFromLastVersion,
	setGenerateFromLastTemplate,
	setGenerateFromLastVersion,
	setTemplateDate,
	setVersionDate,
	t,
	templateDate,
	versionDate,
}: DateTimeOptionsProps) => {
	return (
		<ObjectTypesFieldSet>
			<StyledLegend>{t('GENERATION_DATE_TIME_OPTIONS')}</StyledLegend>
			<div className="flex mb-[-0.3rem] flex-col w-full">
				<div className="py-2 flex flex-row items-center justify-between">
					<div className="flex flex-row items-center gap-2">
						<Typography variant="body2" className="min-w-20">
							{capitalize(t('VERSION'))}
						</Typography>
						<ToggleButtonGroup
							value={generateFromLastVersion ? 'latest' : 'historical'}
							exclusive
							size="small"
							onChange={(_, value) => {
								if (value !== null) {
									setGenerateFromLastVersion(value === 'latest')
								}
							}}
							sx={{
								'& .MuiToggleButton-root': {
									border: 'none',
									textTransform: 'none',
								},
							}}
						>
							<ToggleButton value="latest">{t('LATEST')}</ToggleButton>
							<ToggleButton value="historical">{t('HISTORICAL')}</ToggleButton>
						</ToggleButtonGroup>
					</div>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DateTimePicker
							label={t('SELECT_DATE')}
							value={versionDate || new Date()}
							onChange={(date) => setVersionDate(date)}
							timeSteps={{ minutes: 1, seconds: 1 }}
							views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
							sx={DATE_PICKER_SX}
							slotProps={DATE_PICKER_SLOT_PROPS}
							disabled={generateFromLastVersion}
						/>
					</LocalizationProvider>
				</div>
				<Divider />
				<div className="pt-[0.8rem] flex flex-row items-center justify-between">
					<div className="flex flex-row items-center gap-2">
						<Typography variant="body2" className="min-w-20">
							{capitalize(t('TEMPLATE'))}
						</Typography>
						<ToggleButtonGroup
							value={generateFromLastTemplate ? 'latest' : 'historical'}
							exclusive
							size="small"
							onChange={(_, value) => {
								if (value !== null) {
									setGenerateFromLastTemplate(value === 'latest')
								}
							}}
							sx={{
								'& .MuiToggleButton-root': {
									border: 'none',
									textTransform: 'none',
								},
							}}
						>
							<ToggleButton value="latest">{t('LATEST')}</ToggleButton>
							<ToggleButton value="historical">{t('HISTORICAL')}</ToggleButton>
						</ToggleButtonGroup>
					</div>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DateTimePicker
							label={t('SELECT_DATE')}
							value={templateDate || new Date()}
							onChange={(date) => setTemplateDate(date)}
							timeSteps={{ minutes: 1, seconds: 1 }}
							views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
							sx={DATE_PICKER_SX}
							slotProps={DATE_PICKER_SLOT_PROPS}
							disabled={generateFromLastTemplate}
						/>
					</LocalizationProvider>
				</div>
			</div>
		</ObjectTypesFieldSet>
	)
}
